import React, { useEffect, useState, useMemo } from 'react';
import { auth, db } from '../../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { UploadCloud } from 'lucide-react';
import DateCard from '../common/DateCard';
import FilterBar from '../common/FilterBar'; // <-- Import the FilterBar
import { HeaderGrid, UploadButton } from './ClientDashboard.styles';
import { PageTitle } from '../common/common.styles';
import { ClientGrid } from './DataEntryDashboard.styles';

const ClientDashboard = () => {
  const [documents, setDocuments] = useState([]);
  const user = auth.currentUser;
  const navigate = useNavigate();

  // --- State for all our filters ---
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'documents'), where('userId', '==', user.uid));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setDocuments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
      return () => unsubscribe();
    }
  }, [user]);

  // This memoized function now filters the documents before grouping them by month
  const docsByMonth = useMemo(() => {
    const filtered = documents.filter(doc => {
      const docDate = doc.uploadedAt.toDate();
      const matchesSearch = doc.fileName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'All' || doc.status === statusFilter;
      const matchesStartDate = !startDate || docDate >= startDate;
      const matchesEndDate = !endDate || docDate < new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
      
      return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate;
    });

    return filtered.reduce((acc, doc) => {
      const monthYear = format(doc.uploadedAt.toDate(), 'MMMM yyyy');
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(doc);
      return acc;
    }, {});
  }, [documents, searchTerm, statusFilter, startDate, endDate]);

  const handleDateCardClick = (monthDocs) => {
    navigate('/client/documents', { state: { documents: monthDocs } });
  };

  return (
    <div>
      <HeaderGrid>
        <PageTitle>Client Dashboard</PageTitle>
        <UploadButton to="/client/upload"><UploadCloud size={20} /> Upload New</UploadButton>
      </HeaderGrid>

      {/* --- ADD THE FILTER BAR TO THE UI --- */}
      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
      
      <ClientGrid>
        {Object.keys(docsByMonth).length > 0 ? (
          Object.keys(docsByMonth).map(monthYear => {
            const monthDocs = docsByMonth[monthYear];
            const stats = {
              total: monthDocs.length,
              pending: monthDocs.filter(d => d.status === 'Pending').length,
            };
            if (stats.total > 0) {
              return (
                <DateCard 
                  key={monthYear}
                  monthYear={monthYear}
                  stats={stats}
                  onClick={() => handleDateCardClick(monthDocs)}
                />
              );
            }
            return null;
          })
        ) : (
          <p>No documents found matching your filters.</p>
        )}
      </ClientGrid>
    </div>
  );
};

export default ClientDashboard;